import { Search, Truck } from "lucide-react";
import { useMemo, useState } from "react";
import { STATE_META, fmtCoords, vehicleState } from "@/lib/fleet";

export const VehicleSidebar = ({ vehicles, selectedId, onSelect }) => {
  const [q, setQ] = useState("");

  const filtered = useMemo(
    () =>
      vehicles.filter((v) =>
        `${v.plate_number} ${v.current_location_name || ""}`.toLowerCase().includes(q.toLowerCase())
      ),
    [vehicles, q]
  );

  const moving = vehicles.filter((v) => vehicleState(v) === "moving").length;
  const counts = vehicles.reduce((acc, v) => {
    const s = vehicleState(v);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex h-full flex-col bg-[#0A0A0A]" data-testid="vehicle-sidebar">
      <div className="border-b border-white/10 px-5 py-4">
        <div className="flex items-baseline justify-between">
          <p className="overline">fleet roster</p>
          <p className="text-xs text-[#A1A1AA]">
            <span className="text-[#00FF66]">{moving}</span> / {vehicles.length} moving
          </p>
        </div>
        <div className="mt-2 flex flex-wrap gap-1" data-testid="status-feed-summary">
          {Object.entries(STATE_META)
            .filter(([key]) => counts[key])
            .map(([key, meta]) => (
              <span
                key={key}
                data-testid={`status-count-${key}`}
                className="border px-1.5 py-0.5 text-[9px] tracking-[0.12em]"
                style={{ color: meta.color, borderColor: `${meta.color}44` }}
              >
                {meta.label} {counts[key]}
              </span>
            ))}
        </div>
        <div className="mt-3 flex items-center gap-2 border border-white/10 bg-[#121212] px-3 py-2">
          <Search size={13} className="text-[#52525B]" />
          <input
            data-testid="vehicle-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="SEARCH PLATE / CITY"
            className="w-full bg-transparent text-[11px] uppercase tracking-[0.15em] outline-none placeholder:text-[#3f3f46]"
          />
        </div>
      </div>

      <div className="doot-scroll flex-1 overflow-y-auto">
        {filtered.length === 0 && (
          <p className="px-5 py-8 text-xs text-[#52525B]" data-testid="vehicle-list-empty">
            NO VEHICLES MATCH
          </p>
        )}
        {filtered.map((v) => {
          const meta = STATE_META[vehicleState(v)];
          const active = v.id === selectedId;
          return (
            <button
              key={v.id}
              data-testid={`vehicle-list-item-${v.plate_number}`}
              onClick={() => onSelect(v.id)}
              className={`group block w-full border-b border-white/5 px-5 py-4 text-left transition-colors duration-150 ${
                active ? "bg-[#1A1A1A]" : "hover:bg-[#141414]"
              }`}
              style={active ? { borderLeft: "2px solid #00F0FF" } : { borderLeft: "2px solid transparent" }}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block h-2 w-2"
                    style={{ background: meta.color }}
                    aria-hidden
                  />
                  <span
                    className={`font-head text-sm font-bold tracking-tight ${
                      active ? "text-[#00F0FF]" : "text-white group-hover:text-[#00F0FF]"
                    }`}
                  >
                    {v.plate_number}
                  </span>
                </div>
                <span
                  className="border px-1.5 py-0.5 text-[9px] tracking-[0.15em]"
                  style={{ color: meta.color, borderColor: `${meta.color}55` }}
                >
                  {meta.label}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-[11px] text-[#A1A1AA]">
                <Truck size={11} className="text-[#52525B]" />
                {v.current_location_name || fmtCoords(v.current_location)}
              </div>
              <div className="mt-1 flex gap-4 text-[10px] text-[#52525B]">
                <span>IGN {v.ignition_state?.toUpperCase()}</span>
                <span>{Number(v.speed_kmh || 0).toFixed(0)} KM/H</span>
              </div>
              {v.active_trip?.halt && null}
              {v.governance_breach && (
                <p
                  className="mt-1 text-[10px] tracking-[0.12em] text-[#FF3B30]"
                  data-testid={`vehicle-breach-${v.plate_number}`}
                >
                  OVER GOVERNED LIMIT · {v.governance_breach.toUpperCase()}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
